import { useEffect, useState, useCallback } from 'react';
import { Accessibility, X, Type, Contrast, Underline, PauseCircle, RotateCcw, ALargeSmall, Eye, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const FEATURES = [
  { key: 'large-text', label: 'Large Text', icon: Type, cls: 'bp-a11y-large-text' },
  { key: 'xl-text', label: 'Extra Large', icon: ALargeSmall, cls: 'bp-a11y-xl-text' },
  { key: 'high-contrast', label: 'High Contrast', icon: Contrast, cls: 'bp-a11y-high-contrast' },
  { key: 'readable-font', label: 'Readable Font', icon: Eye, cls: 'bp-a11y-readable-font' },
  { key: 'underline-links', label: 'Underline Links', icon: Underline, cls: 'bp-a11y-underline-links' },
  { key: 'pause-motion', label: 'Pause Animations', icon: PauseCircle, cls: 'bp-a11y-pause-motion' },
];

const STORAGE_KEY = 'bp_a11y_prefs_v1';

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState({});

  useEffect(() => {
    try { setActive(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    FEATURES.forEach((f) => { if (active[f.key]) root.classList.add(f.cls); else root.classList.remove(f.cls); });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(active)); } catch { /* ignore */ }
  }, [active]);

  const toggle = useCallback((key) => {
    setActive((a) => {
      const next = { ...a, [key]: !a[key] };
      if (key === 'large-text' && next['large-text']) next['xl-text'] = false;
      if (key === 'xl-text' && next['xl-text']) next['large-text'] = false;
      return next;
    });
  }, []);

  const reset = () => setActive({});

  return (
    <>
      <button
        type="button"
        data-testid="a11y-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close accessibility menu' : 'Open accessibility menu'}
        aria-expanded={open}
        className="fixed z-50 bottom-5 left-5 w-14 h-14 rounded-full bg-bp-blue text-white shadow-lg flex items-center justify-center hover:bg-bp-blue-dark focus:outline-none focus:ring-4 focus:ring-bp-blue/30 transition-all hover:scale-110"
      >
        <Accessibility size={26} strokeWidth={2.2} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Accessibility options"
          data-testid="a11y-panel"
          className="fixed z-50 bottom-24 left-5 w-[min(94vw,360px)] bg-white border border-bp-slate-200 rounded-2xl overflow-hidden shadow-2xl"
        >
          <div className="px-5 py-4 bg-bp-blue text-white flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-white/80">Accessibility</div>
              <div className="font-display font-bold text-lg leading-tight mt-0.5">Adjust your experience</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="a11y-close"
              className="w-9 h-9 rounded-full hover:bg-white/15 inline-flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-4 grid grid-cols-2 gap-2.5">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              const on = !!active[f.key];
              return (
                <button
                  type="button"
                  key={f.key}
                  data-testid={`a11y-feature-${f.key}`}
                  onClick={() => toggle(f.key)}
                  aria-pressed={on}
                  className={`flex flex-col items-start gap-2 p-3 text-left rounded-xl border transition-colors ${
                    on
                      ? 'bg-bp-blue text-white border-bp-blue'
                      : 'bg-white text-bp-ink border-bp-slate-200 hover:border-bp-blue'
                  }`}
                >
                  <Icon size={18} className={on ? 'text-white' : 'text-bp-blue'} />
                  <span className="font-semibold text-[13px] leading-tight">{f.label}</span>
                </button>
              );
            })}
          </div>

          <div className="px-4 pb-4 flex items-center justify-between gap-3">
            <button type="button" data-testid="a11y-reset" onClick={reset} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-bp-slate hover:text-bp-blue">
              <RotateCcw size={13} /> Reset
            </button>
            <Link to="/accessibility" onClick={() => setOpen(false)} data-testid="a11y-statement-link" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-bp-blue hover:underline">
              Accessibility Statement <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
